import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import netSuiteLogo from '../../assets/logos/netsuite-logo.svg';
import { connectorAPI } from '../../utils/api';
import { 
  faTimes, 
  faEye, 
  faEyeSlash, 
  faCheckCircle,
  faExclamationCircle,
  faNetworkWired,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const NetSuiteConnectorForm = ({ isOpen, onClose, onSave, editingConnector = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    instanceName: '',
    signatureMethod: 'HMAC-SHA1',
    consumerKey: '',
    consumerSecret: '',
    accessToken: '',
    tokenSecret: '',
    realm: ''
  });

  const [showSensitive, setShowSensitive] = useState({
    consumerSecret: false,
    tokenSecret: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  const signatureMethods = [
    { value: 'HMAC-SHA1', label: 'HMAC-SHA1 (Recommended)' },
    { value: 'HMAC-SHA256', label: 'HMAC-SHA256' }
  ];

  // Populate form data when editing
  useEffect(() => {
    if (editingConnector && isOpen) {
      // Now we get credentials back from API for editing
      const credentials = editingConnector.credentials || {};
      setFormData({
        name: editingConnector.name || '',
        instanceName: editingConnector.instance_name || '',
        signatureMethod: credentials.signature_method || 'HMAC-SHA1',
        consumerKey: credentials.consumer_key || '',
        consumerSecret: credentials.consumer_secret || '',
        accessToken: credentials.token_id || '', // Backend uses token_id
        tokenSecret: credentials.token_secret || '',
        realm: credentials.realm || ''
      });
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        name: '',
        instanceName: '',
        signatureMethod: 'HMAC-SHA1',
        consumerKey: '',
        consumerSecret: '',
        accessToken: '',
        tokenSecret: '',
        realm: ''
      });
    }
  }, [editingConnector, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.name.trim()) newErrors.name = 'Connection name is required';
    if (!formData.consumerKey.trim()) newErrors.consumerKey = 'Consumer Key is required';
    if (!formData.consumerSecret.trim()) newErrors.consumerSecret = 'Consumer Secret is required';
    if (!formData.accessToken.trim()) newErrors.accessToken = 'Access Token is required';
    if (!formData.tokenSecret.trim()) newErrors.tokenSecret = 'Token Secret is required';
    if (!formData.realm.trim()) newErrors.realm = 'Realm is required';

    // Format validation
    if (formData.realm && !formData.realm.match(/^[A-Z0-9_]+$/)) {
      newErrors.realm = 'Realm should contain only uppercase letters, numbers, and underscores';
    }

    if (formData.name && formData.name.length < 3) {
      newErrors.name = 'Connection name must be at least 3 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const toggleSensitive = (field) => {
    setShowSensitive(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before testing');
      return;
    }

    setTestingConnection(true);
    try {
      // First save/update the connector if editing
      let connectorName = formData.name;
      
      if (editingConnector) {
        // Update existing connector for testing
        const connectorData = {
          name: formData.name,
          service_type: 'netsuite',
          credentials: {
            signature_method: formData.signatureMethod,
            consumer_key: formData.consumerKey,
            consumer_secret: formData.consumerSecret,
            token_id: formData.accessToken,
            token_secret: formData.tokenSecret,
            realm: formData.realm
          }
        };
        await connectorAPI.updateConnector(editingConnector.name, connectorData);
        connectorName = editingConnector.name;
      }
      
      // Test the connection
      const result = await connectorAPI.testConnection(connectorName);
      
      if (result.success) {
        toast.success(result.message || 'NetSuite connection test successful!');
      } else {
        toast.error(result.message || 'Connection test failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Connection test error:', error);
      const errorMessage = error.response?.data?.detail?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Connection test failed';
      toast.error(errorMessage);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix all errors before saving');
      return;
    }

    setIsLoading(true);
    try {
      const connectorData = {
        name: formData.name,
        instance_name: formData.instanceName || undefined, // Include instance_name if provided
        service_type: 'netsuite',
        credentials: {
          signature_method: formData.signatureMethod,
          consumer_key: formData.consumerKey,
          consumer_secret: formData.consumerSecret,
          token_id: formData.accessToken,
          token_secret: formData.tokenSecret,
          realm: formData.realm
        }
      };

      await onSave(connectorData);
      toast.success(editingConnector ? 'NetSuite connector updated successfully!' : 'NetSuite connector created successfully!');
      handleClose();
    } catch (error) {
      toast.error('Failed to create connector');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      instanceName: '',
      signatureMethod: 'HMAC-SHA1',
      consumerKey: '',
      consumerSecret: '',
      accessToken: '',
      tokenSecret: '',
      realm: ''
    });
    setErrors({});
    setShowSensitive({
      consumerSecret: false,
      tokenSecret: false
    });
    onClose();
  };

  const renderField = (field, label, type = 'text', options = {}) => {
    const isSensitive = ['consumerSecret', 'tokenSecret'].includes(field);
    const inputType = isSensitive && !showSensitive[field] ? 'password' : 'text';

    return (
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label} <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={inputType}
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className={`form-input w-full ${errors[field] ? 'border-red-500' : ''} ${isSensitive ? 'pr-10' : ''}`}
            placeholder={options.placeholder || `Enter ${label.toLowerCase()}`}
            {...options}
          />
          {isSensitive && (
            <button
              type="button"
              onClick={() => toggleSensitive(field)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <FontAwesomeIcon 
                icon={showSensitive[field] ? faEyeSlash : faEye} 
                className="text-sm"
              />
            </button>
          )}
        </div>
        {errors[field] && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-500 text-sm flex items-center"
          >
            <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
            {errors[field]}
          </motion.p>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                  <img 
                    src={netSuiteLogo} 
                    alt="NetSuite Logo"
                    className="w-8 h-5 object-contain"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {editingConnector ? 'Edit NetSuite Connector' : 'NetSuite Connector'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {editingConnector ? 'Update OAuth1 authentication for NetSuite integration' : 'Configure OAuth1 authentication for NetSuite integration'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Connection Name */}
              {renderField('name', 'Connection Name', 'text', {
                placeholder: 'e.g., NetSuite Production'
              })}

              {/* Instance Name - Optional unique identifier for multiple instances */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Instance Name <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.instanceName}
                  onChange={(e) => handleInputChange('instanceName', e.target.value)}
                  className="form-input w-full"
                  placeholder="e.g., production, sandbox"
                  disabled={!!editingConnector}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Unique identifier for multiple connectors of the same type
                </p>
              </div>
            </div>

            {/* Signature Method */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Signature Method <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.signatureMethod}
                onChange={(e) => handleInputChange('signatureMethod', e.target.value)}
                className="form-input w-full"
              >
                {signatureMethods.map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Consumer Key */}
              {renderField('consumerKey', 'Consumer Key', 'text', {
                placeholder: 'Enter consumer key from NetSuite'
              })}

              {/* Consumer Secret */}
              {renderField('consumerSecret', 'Consumer Secret')}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Access Token */}
              {renderField('accessToken', 'Access Token', 'text', {
                placeholder: 'Enter access token'
              })}

              {/* Token Secret */}
              {renderField('tokenSecret', 'Token Secret')}
            </div>

            {/* Realm */}
            {renderField('realm', 'Realm', 'text', {
              placeholder: 'e.g., TSTDRV123456'
            })}

            {/* Helper Text */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                How to get NetSuite credentials:
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>• Go to Setup → Integration → Web Services Preferences</li>
                <li>• Create new integration record</li>
                <li>• Generate Consumer Key/Secret and Access Token/Secret</li>
                <li>• Realm is your NetSuite account ID</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between">
              <button
                onClick={handleTestConnection}
                disabled={testingConnection}
                className={`btn ${testingConnection ? 'btn-disabled' : 'btn-secondary'}`}
              >
                {testingConnection ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                    Test Connection
                  </>
                )}
              </button>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleClose}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className={`btn ${isLoading ? 'btn-disabled' : 'btn-primary'}`}
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      {editingConnector ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingConnector ? 'Update Connector' : 'Create Connector'
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default NetSuiteConnectorForm;